import { useTranslation } from 'react-i18next';
import {
  StopRegistryInfoSpotInput,
  StopRegistryPosterInput,
  useUpdateInfoSpotMutation,
} from '../../../../../../../generated/graphql';
import {
  KnownValueKey,
  NullOptionEnum,
  mapPointToStopRegistryGeoJSON,
  showDangerToastWithError,
} from '../../../../../../../utils';
import { mapPurposeToString } from '../../../../../Components/InfoSpots';
import { TerminalInfoSpotFormState } from '../Types';

function mapNullEnumOption<T>(
  value: T | NullOptionEnum | null | undefined,
): T | null {
  if (value === null || value === undefined || value === NullOptionEnum.Null) {
    return null;
  }

  return value;
}

function mapPosterInput(
  poster: TerminalInfoSpotFormState['poster'],
): Array<StopRegistryPosterInput> | null {
  if (!poster?.length) {
    return null;
  }

  return poster.map(
    ({ id, label, size, lines }, index): StopRegistryPosterInput => ({
      id,
      label: mapPurposeToString(label),
      width: size.width,
      height: size.height,
      lines,
      keyValues: [
        {
          key: KnownValueKey.SortOrder,
          values: [index.toString()],
        },
      ],
    }),
  );
}

function mapTerminalInfoSpotFormToInput(
  infoSpot: TerminalInfoSpotFormState,
): StopRegistryInfoSpotInput {
  return {
    id: infoSpot.infoSpotId,
    backlight: infoSpot.backlight,
    description: {
      lang: infoSpot.description?.lang,
      value: infoSpot.description?.value,
    },
    geometry: mapPointToStopRegistryGeoJSON(infoSpot),
    displayType: mapNullEnumOption(infoSpot.displayType),
    floor: infoSpot.floor,
    label: infoSpot.label,
    width: infoSpot.size.width,
    height: infoSpot.size.height,
    infoSpotLocations: infoSpot.infoSpotLocations,
    infoSpotType: mapNullEnumOption(infoSpot.infoSpotType),
    intendedUser: mapNullEnumOption(infoSpot.intendedUser),
    railInformation: infoSpot.railInformation,
    speechProperty: infoSpot.speechProperty,
    zoneLabel: infoSpot.zoneLabel,
    poster: mapPosterInput(infoSpot.poster),
  };
}

function handleDeletions(
  infoSpot: TerminalInfoSpotFormState,
): TerminalInfoSpotFormState {
  return {
    ...infoSpot,
    poster:
      infoSpot.poster?.filter((poster) => !poster.toBeDeletedPoster) ?? [],
    infoSpotLocations: infoSpot.toBeDeleted ? [] : infoSpot.infoSpotLocations,
  };
}

export function useEditTerminalInfoSpots() {
  const { t } = useTranslation();
  const [updateInfoSpotMutation] = useUpdateInfoSpotMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      'GetParentStopPlaceDetails',
      'GetLatestStopPlaceChange',
      'GetStopPlaceChangeHistory',
    ],
  });

  const saveTerminalInfoSpots = async (params: {
    state: TerminalInfoSpotFormState;
  }): Promise<string | undefined> => {
    const response = await updateInfoSpotMutation({
      variables: {
        input: mapTerminalInfoSpotFormToInput(handleDeletions(params.state)),
      },
    });

    const rawInfoSpots = response.data?.stop_registry?.mutateInfoSpots;
    if (!rawInfoSpots || rawInfoSpots.length === 0) {
      return undefined;
    }

    return rawInfoSpots[0]?.id ?? undefined;
  };

  // default handler that can be used to show error messages as toast
  // in case an exception is thrown
  const defaultErrorHandler = (err: Error) => {
    showDangerToastWithError(
      t(($) => $.errors.saveFailed),
      err,
    );
  };

  return {
    saveTerminalInfoSpots,
    defaultErrorHandler,
  };
}
